import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Grid, Typography, Paper, Stack, Button, Chip, Avatar, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, useTheme,
  Tab, Tabs, Divider, IconButton, Tooltip, Alert
} from "@mui/material";
import {
  CheckCircleOutlined, CancelOutlined, VisibilityOutlined,
  DescriptionOutlined, BusinessOutlined, NotificationsActiveOutlined,
  BlockOutlined, RestoreOutlined
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { getAllPartners, getKYCQueue, approveKYC, rejectKYC, suspendPartner, reactivatePartner } from "../../store/slices/partnerSlice";
import { toast } from "react-toastify";


const AdminPartners = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { user: clerkUser } = useUser();
  const { allPartners, kycQueue, loading } = useSelector(s => s.partner);
  const isDark = theme.palette.mode === 'dark';

  const [tab, setTab] = useState(0);
  const [detailDialog, setDetailDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendDialog, setSuspendDialog] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  useEffect(() => {
    dispatch(getAllPartners({}));
    dispatch(getKYCQueue({}));
  }, [dispatch]);

  const handleApprove = async (partnerId) => {
    if (!window.confirm("Approve this partner's KYC? Their theater will be activated.")) return;
    try {
      await dispatch(approveKYC({ partnerId, reviewedBy: clerkUser?.id })).unwrap();
      toast.success("✅ Partner KYC Approved & Theater Activated!");
      setDetailDialog(null);
      dispatch(getAllPartners({}));
    } catch (err) { toast.error("Failed to approve"); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return toast.error("Please enter rejection reason");
    try {
      await dispatch(rejectKYC({ partnerId: rejectDialog._id, reviewedBy: clerkUser?.id, reason: rejectReason })).unwrap();
      toast.success("KYC Rejected");
      setRejectDialog(null); setRejectReason('');
      dispatch(getAllPartners({}));
    } catch (err) { toast.error("Failed to reject"); }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) return toast.error("Please enter suspension reason");
    try {
      await dispatch(suspendPartner({ partnerId: suspendDialog._id, reason: suspendReason })).unwrap();
      toast.success("Partner Suspended");
      setSuspendDialog(null); setSuspendReason('');
      dispatch(getAllPartners({}));
    } catch (err) { toast.error("Failed to suspend"); }
  };

  const handleReactivate = async (partnerId) => {
    if (!window.confirm("Reactivate this partner? Their theaters will be re-enabled.")) return;
    try {
      await dispatch(reactivatePartner({ partnerId })).unwrap();
      toast.success("Partner Reactivated");
      setDetailDialog(null);
      dispatch(getAllPartners({}));
    } catch (err) { toast.error("Failed to reactivate"); }
  };

  const kycColors = { APPROVED: '#56ca00', UNDER_REVIEW: '#ffb400', PENDING_SUBMISSION: '#8a8d93', REJECTED: '#ff4c51', SUSPENDED: '#8b5cf6' };
  const cSx = { p: 2.5, borderRadius: '14px', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`, transition: '0.25s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'}` } };

  const displayList = tab === 0 ? kycQueue : tab === 1 ? allPartners : tab === 2 ? allPartners.filter(p => p.kycStatus === 'APPROVED') : allPartners.filter(p => p.kycStatus === 'SUSPENDED');

  return (
    <Box>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, mb: 0.3 }}>Partner Management</Typography>
        <Typography sx={{ fontSize: "0.83rem", color: theme.palette.text.secondary, opacity: 0.6, mb: 3 }}>Review applications, approve KYC, suspend partners</Typography>

        {kycQueue.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Alert severity="warning" icon={<NotificationsActiveOutlined />}
              sx={{ mb: 3, borderRadius: '12px', fontWeight: 600, fontSize: '0.85rem', border: '1px solid rgba(255,180,0,0.2)' }}>
              🔔 {kycQueue.length} partner application{kycQueue.length > 1 ? 's' : ''} awaiting KYC review!
            </Alert>
          </motion.div>
        )}

        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { l: 'Total Partners', v: allPartners.length, c: '#6366f1' },
            { l: 'KYC Pending', v: kycQueue.length, c: '#ffb400' },
            { l: 'Approved', v: allPartners.filter(p => p.kycStatus === 'APPROVED').length, c: '#56ca00' },
            { l: 'Suspended/Blocked', v: allPartners.filter(p => p.kycStatus === 'SUSPENDED').length, c: '#8b5cf6' },
          ].map((s, i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Paper elevation={0} sx={{ ...cSx, p: 2, textAlign: 'center', '&:hover': { transform: 'none' } }}>
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: s.c }}>{s.v}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary, opacity: 0.5, fontWeight: 600, textTransform: 'uppercase' }}>{s.l}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={0} sx={{ borderRadius: '12px', mb: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}` }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' } }}>
            <Tab label={<Stack direction="row" spacing={0.5} alignItems="center"><span>🔔 KYC Queue ({kycQueue.length})</span></Stack>} />
            <Tab label={`All Partners (${allPartners.length})`} />
            <Tab label="Approved" />
            <Tab label="Suspended" />
          </Tabs>
        </Paper>

        {displayList.length === 0 ? (
          <Paper elevation={0} sx={{ ...cSx, p: 4, textAlign: 'center', '&:hover': { transform: 'none' } }}>
            <Typography sx={{ opacity: 0.4, fontWeight: 600 }}>
              {tab === 0 ? '✅ No pending KYC reviews' : 'No partners found'}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {displayList.map((p, idx) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={p._id}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                  <Paper elevation={0} sx={cSx}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar sx={{ bgcolor: '#6366f1', width: 40, height: 40, fontSize: '0.85rem', fontWeight: 700 }}>
                        {(p.userName || 'P')[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.userName || 'Unknown'}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>{p.userEmail}</Typography>
                      </Box>
                      <Chip label={p.kycStatus?.replace(/_/g,' ')} size="small"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${kycColors[p.kycStatus]||'#8a8d93'}15`, color: kycColors[p.kycStatus]||'#8a8d93' }} />
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>🏢 {p.businessDetails?.theaterDisplayName || 'Not set'}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>📍 {p.businessDetails?.address?.city || 'N/A'}, {p.businessDetails?.address?.state || ''}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>📦 Plan: <b>{p.currentPlan || 'Starter'}</b> • Centers: {p.theaters?.length || 0}</Typography>
                      {p.kycStatus === 'SUSPENDED' && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#ff4c51', mt: 1, fontWeight: 600 }}>⚠️ {p.suspensionReason}</Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setDetailDialog(p)}
                          sx={{ bgcolor: 'rgba(99,102,241,0.08)', color: '#6366f1', borderRadius: '8px' }}>
                          <VisibilityOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {(p.kycStatus === 'UNDER_REVIEW' || p.kycStatus === 'PENDING_SUBMISSION' || p.kycStatus === 'REJECTED') && (
                        <>
                          <Button size="small" variant="contained" onClick={() => handleApprove(p._id)}
                            sx={{ flex: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', bgcolor: '#56ca00', '&:hover': { bgcolor: '#4bab00' } }}>
                            Approve
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => setRejectDialog(p)}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', color: '#ff4c51', borderColor: '#ff4c51' }}>
                            Reject
                          </Button>
                        </>
                      )}
                      {(p.kycStatus === 'APPROVED' || p.kycStatus === 'REJECTED') && (
                        <Button size="small" variant="outlined" onClick={() => setSuspendDialog(p)}
                          sx={{ flex: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', color: '#8b5cf6', borderColor: '#8b5cf6' }}>
                          <BlockOutlined sx={{ fontSize: 14, mr: 0.5 }} /> Suspend
                        </Button>
                      )}
                      {p.kycStatus === 'SUSPENDED' && (
                        <Button size="small" variant="contained" onClick={() => handleReactivate(p._id)}
                          sx={{ flex: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', bgcolor: '#56ca00', '&:hover': { bgcolor: '#4bab00' } }}>
                          <RestoreOutlined sx={{ fontSize: 14, mr: 0.5 }} /> Reactivate
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Partner Details — {detailDialog?.userName}</DialogTitle>
          <DialogContent>
            {detailDialog && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5, color: '#6366f1' }}>📋 Business Info</Typography>
                  {[
                    ['Name', detailDialog.userName],
                    ['Email', detailDialog.userEmail],
                    ['Theater', detailDialog.businessDetails?.theaterDisplayName],
                    ['Legal Entity', detailDialog.businessDetails?.legalEntityName],
                    ['Business Type', detailDialog.businessDetails?.businessType],
                    ['GST', detailDialog.businessDetails?.gstNumber],
                    ['PAN', detailDialog.businessDetails?.panNumber],
                    ['License', detailDialog.businessDetails?.cinemaLicenseNumber],
                    ['Registered Centers', detailDialog.theaters?.length || 0],
                  ].map(([label, value]) => (
                    <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.5, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                      <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>{label}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{value || '—'}</Typography>
                    </Stack>
                  ))}
                  {detailDialog.kycStatus === 'SUSPENDED' && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
                      <b>Suspended:</b> {detailDialog.suspensionReason}
                    </Alert>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5, color: '#6366f1' }}>📍 Address</Typography>
                  <Typography sx={{ fontSize: '0.85rem' }}>
                    {detailDialog.businessDetails?.address?.line1 || ''}<br />
                    {detailDialog.businessDetails?.address?.city || ''}, {detailDialog.businessDetails?.address?.state || ''}
                  </Typography>

                  <Typography sx={{ fontWeight: 700, mb: 1.5, mt: 3, color: '#6366f1' }}>📄 Documents</Typography>
                  <Stack spacing={1}>
                    {detailDialog.documents?.map((d, i) => (
                      <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.2, borderRadius: '8px', bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{d.type?.replace(/_/g, ' ')}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: theme.palette.text.secondary }}>{d.fileName || 'document.pdf'}</Typography>
                        </Box>
                        {d.fileUrl && (
                          <Button size="small" component="a" href={d.fileUrl} target="_blank" startIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 700 }}>
                            View
                          </Button>
                        )}
                      </Stack>
                    )) || <Typography sx={{ fontSize: '0.8rem', opacity: 0.4 }}>No documents uploaded</Typography>}
                  </Stack>

                  <Typography sx={{ fontWeight: 700, mb: 1.5, mt: 3, color: '#6366f1' }}>🏢 Associated Theaters</Typography>
                  <Stack spacing={1}>
                    {detailDialog.theaters?.map((t, i) => (
                      <Paper key={i} elevation={0} sx={{ p: 1.5, borderRadius: '10px', bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{t.name}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', opacity: 0.6 }}>{t.address?.city}</Typography>
                          </Box>
                          <Chip label={t.status?.isVerified ? 'Verified' : 'Unverified'} size="small"
                            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, 
                              bgcolor: t.status?.isVerified ? 'rgba(86,202,0,0.1)' : 'rgba(255,180,0,0.1)',
                              color: t.status?.isVerified ? '#56ca00' : '#ffb400'
                            }} />
                        </Stack>
                      </Paper>
                    )) || <Typography sx={{ fontSize: '0.8rem', opacity: 0.4 }}>No theaters registered</Typography>}
                  </Stack>

                  <Typography sx={{ fontWeight: 700, mb: 1.5, mt: 3, color: '#6366f1' }}>🏦 Bank</Typography>
                  <Stack sx={{ py: 0.3 }}><Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{detailDialog.bankDetails?.accountHolderName}</Typography></Stack>
                  <Stack sx={{ py: 0.3 }}><Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{detailDialog.bankDetails?.ifscCode}</Typography></Stack>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
            <Box>
              {(detailDialog?.kycStatus === 'UNDER_REVIEW' || detailDialog?.kycStatus === 'PENDING_SUBMISSION' || detailDialog?.kycStatus === 'REJECTED') && (
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" color="success" onClick={() => handleApprove(detailDialog._id)} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>Approve KYC</Button>
                  <Button variant="outlined" color="error" onClick={() => { setRejectDialog(detailDialog); setDetailDialog(null); }} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>Reject</Button>
                </Stack>
              )}
            </Box>
            <Button onClick={() => setDetailDialog(null)} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>Dismiss</Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontWeight: 700, color: '#ff4c51' }}>Reject KYC — {rejectDialog?.userName}</DialogTitle>
          <DialogContent>
            <TextField fullWidth multiline rows={3} placeholder="Rejection reason..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRejectDialog(null)} sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={handleReject} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: '#ff4c51' }}>Reject</Button>
          </DialogActions>
        </Dialog>

        {/* Suspend Dialog */}
        <Dialog open={!!suspendDialog} onClose={() => setSuspendDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontWeight: 700, color: '#8b5cf6' }}>Suspend Partner — {suspendDialog?.userName}</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.85rem', mb: 2 }}>This will immediately block their dashboard and take their theaters offline.</Typography>
            <TextField fullWidth multiline rows={3} placeholder="Suspension reason (sent to partner)..." value={suspendReason} onChange={e => setSuspendReason(e.target.value)} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSuspendDialog(null)} sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSuspend} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: '#8b5cf6' }}>Suspend Partner</Button>
          </DialogActions>
        </Dialog>

      </Box>
  );
};

export default AdminPartners;
